import { pdfWatermarkDocumentTypesByClient } from '@root/config/tenant.pdf-watermark';
import { tenantConfig } from '@root/config/tenants';
import type { TenantConfig, TenantName } from '@root/config/types';
import type { Environment } from '@root/helpers/env/environments';

/** PPL environments that behave as test environments; only isPPLTestEnv reads this. */
const pplCbTestEnvs: ReadonlySet<Environment> = new Set(['jit', 'dad', 'sad', 'stg', 'dev', 'dprf', 'perf', 'uat']);

/** Resolved by `moduleInputs` for the running app and by `scripts/feature-flags` per environment. */
export interface FeatureFlagInputs {
  environment: Environment;
  client: TenantName;
  useOpenSearch: string | undefined;
  disableRecordCreation: string | undefined;
  pbqaEvalEnabled: string | undefined;
  renewalSummaryEnabled: string | undefined;
  tenantConfig: TenantConfig;
}

interface FeatureFlagDefinition {
  /** Short what-it-controls description, surfaced in the gitops feature-flags matrix. */
  description: string;
  /** Not a product feature — an internal/test marker. Rendered apart in the matrix. */
  internal?: true;
  check: (inputs: FeatureFlagInputs) => boolean | undefined;
}

const isPPL = (inputs: FeatureFlagInputs) => inputs.client === 'ct-ppl';

const FEATURE_FLAGS = {
  Debugger: {
    description: 'In-app developer debug tooling',
    check: (i) => ['local', 'dev', 'staging', 'ephemeral'].includes(i.environment),
  },
  Endorsements: {
    description: 'Endorsement (mid-term adjustment) workflow',
    check: (i) => ['local'].includes(i.environment) || i.tenantConfig.isEndorsementEnabled,
  },
  Attachments: {
    description: 'File attachments on submissions',
    check: (i) => ['local'].includes(i.environment) || i.tenantConfig.isAttachmentsEnabled,
  },
  Branding: {
    description: 'Tenant branding documents',
    check: (i) => isPPL(i),
  },
  MalwareScan: {
    description: 'Malware scanning of uploaded files',
    // Disabled in ephemeral envs to stay within the malware-protection plan limit. When off, the
    // post-scan version bump that regenerates the contract PDF never fires (see useAttachmentMutations).
    check: (i) => i.environment !== 'ephemeral',
  },
  SSO: {
    description: 'Single sign-on authentication',
    check: (i) => i.tenantConfig.isSSOEnabled,
  },
  Users: {
    description: 'In-app user management',
    check: (i) => !isPPL(i),
  },
  Teams: {
    description: 'Team management (superadmin-only, non-prod)',
    check: (i) => !isPPL(i) && ['local', 'test', 'dev', 'staging', 'ephemeral'].includes(i.environment),
  },
  SaveClose: {
    description: 'Save & close action in the contract editor',
    check: (i) => isPPL(i),
  },
  FirmOrderStatus: {
    description: 'Firm order contract status',
    check: (i) => !isPPL(i),
  },
  EndorsementReview: {
    description: 'Endorsement review stage',
    check: (i) => !isPPL(i),
  },
  Comments: {
    description: 'Comments on contracts',
    check: (i) => !isPPL(i),
  },
  LibraryBlockActivityLog: {
    description: 'Activity log for library blocks',
    check: (i) => !isPPL(i),
  },
  ClauseDocxImport: {
    description: 'Clause import from DOCX or PDF',
    check: (i) => !isPPL(i),
  },
  TemplateDefaultContent: {
    description: 'Default content on new templates',
    check: (i) => !isPPL(i),
  },
  isPPLTestEnv: {
    description: 'Marks PPL test environments (test-only behaviour)',
    internal: true,
    check: (i) => isPPL(i) && pplCbTestEnvs.has(i.environment),
  },
  /**
   * Contract Review Bot — eval configuration UI. Superadmin-only on the FE.
   * Intentionally gated to non-prod environments during the evaluation period;
   * the whole feature can be removed once the eval wraps up.
   */
  EvalConfigs: {
    description: 'Contract Review Bot: eval configuration UI (superadmin-only)',
    check: (i) => !isPPL(i),
  },
  /**
   * Contract Guidance (PBQA) — the drawer, header badge, contracts-list column
   * and the `GET /feature_access` cohort lookup behind them. On only where the
   * env file says so: `VITE_PBQA_EVAL_ENABLED` mirrors terraform's
   * `enable_pbqa_eval`, which deploys the eval routes in a handful of PRO envs.
   * Everywhere else the routes do not exist, so the lookup would only 403.
   * Same lifecycle as EvalConfigs and removed once the eval wraps up.
   */
  PbqaEval: {
    description: 'Contract Guidance (PBQA) review drawer, badge and column (superadmin-only)',
    check: (i) => !isPPL(i) && i.pbqaEvalEnabled === 'true',
  },
  /**
   * Summary of Changes on a renewal's Compare View. On only where the env file
   * says so: `VITE_RENEWAL_SUMMARY_ENABLED` mirrors terraform's
   * `enable_renewal_summary`, which deploys the routes. Renewals are PRO-only.
   * Within an enabled build the user must also be on the tenant's
   * `renewal_summary` preview allowlist (GET /feature_access).
   */
  RenewalSummary: {
    description: 'Summary of Changes: AI summary of a renewal against its original contract',
    check: (i) => !isPPL(i) && i.renewalSummaryEnabled === 'true',
  },
  TemplateReviewWorkflow: {
    description: 'Template review & approval workflow',
    check: (i) =>
      ['local', 'dev', 'staging', 'test', 'ephemeral'].includes(i.environment) ||
      i.tenantConfig.isTemplateReviewWorkflowEnabled,
  },
  OpenSearch: {
    // The backend resolves this from the Lambda's own USE_OPEN_SEARCH, which terraform sets
    // independently of the env file — the published matrix reflects the frontend build only.
    description: 'OpenSearch-backed search (frontend build-time value)',
    check: (i) => i.useOpenSearch === 'true',
  },
  DisableRecordCreation: {
    description: 'Blocks creation of new records',
    check: (i) => i.disableRecordCreation === 'true',
  },
  DraftWatermark: {
    description: 'Draft watermark on generated PDFs (tenant config)',
    // PPL takes the other branch in create-pdf.ts — `tenantConfig.pdf.watermark`, which
    // `pdfTenantsConfig` leaves unset for ct-ppl — so the document-type map never applies.
    check: (i) => !isPPL(i) && pdfWatermarkDocumentTypesByClient[i.client].length > 0,
  },
} satisfies Record<string, FeatureFlagDefinition>;

export type FeatureFlag = keyof typeof FEATURE_FLAGS;

export interface FeatureFlagManifestEntry {
  name: FeatureFlag;
  description: string;
  internal?: true;
}

/**
 * Every flag with its description, in declaration order. The published feature-flags
 * snapshot aligns its per-environment bitstrings to this order, so it is the one export
 * consumers need — iterate it rather than re-deriving names and descriptions separately.
 */
export const FEATURE_FLAG_MANIFEST: readonly FeatureFlagManifestEntry[] = Object.entries(
  FEATURE_FLAGS as Record<FeatureFlag, FeatureFlagDefinition>,
).map(([name, { description, internal }]) => ({
  name: name as FeatureFlag,
  description,
  ...(internal && { internal }),
}));

/**
 * A tenant-config field left unset for a tenant (most are absent rather than explicitly
 * `false`) reads as off — in the app and in the published matrix alike.
 */
const evaluate = (name: FeatureFlag, inputs: FeatureFlagInputs): boolean => FEATURE_FLAGS[name].check(inputs) ?? false;

/** Evaluates every flag against explicit inputs (used by scripts/feature-flags/). */
export const resolveFeatureFlags = (inputs: FeatureFlagInputs): Record<FeatureFlag, boolean> =>
  Object.fromEntries(FEATURE_FLAG_MANIFEST.map(({ name }) => [name, evaluate(name, inputs)])) as Record<
    FeatureFlag,
    boolean
  >;

const moduleInputs: FeatureFlagInputs = {
  environment: import.meta.env?.VITE_ENVIRONMENT ?? process?.env?.ENVIRONMENT,
  client: import.meta.env?.VITE_CLIENT ?? process?.env?.CLIENT,
  useOpenSearch: import.meta.env?.VITE_USE_OPEN_SEARCH ?? process?.env?.USE_OPEN_SEARCH,
  disableRecordCreation: import.meta.env?.VITE_DISABLE_RECORD_CREATION,
  pbqaEvalEnabled: import.meta.env?.VITE_PBQA_EVAL_ENABLED ?? process?.env?.PBQA_EVAL_ENABLED,
  renewalSummaryEnabled: import.meta.env?.VITE_RENEWAL_SUMMARY_ENABLED ?? process?.env?.RENEWAL_SUMMARY_ENABLED,
  tenantConfig,
};

/** Central place for feature flags management. */
export const getFeatureFlag = (featureFlagName: FeatureFlag) => evaluate(featureFlagName, moduleInputs);
